import AsyncStorage from '@react-native-async-storage/async-storage';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { ProPlanId, ProSource } from '@/lib/pro/plans';

interface ProState {
    /** Status efetivo do Pro (considera override de dev + entitlement do provedor). */
    isPro: boolean;
    /**
     * `true` quando o store já reidratou do disco (último entitlement conhecido do RevenueCat
     * carregado). Antes disso o status é DESCONHECIDO — não se deve rebaixar (tirar features Pro)
     * sem essa resposta. Ver `useIsProDowngraded` e docs/adr/0026.
     */
    hasHydrated: boolean;
    activePlan: ProPlanId | null;
    source: ProSource | null;

    /** Override local para QA/dev — destrava o Pro sem compra real. */
    devOverride: boolean;
    devPlan: ProPlanId | null;
    /** Entitlement vindo do provedor de billing (RevenueCat). */
    remoteIsPro: boolean;
    remotePlan: ProPlanId | null;

    /**
     * `true` quando o usuário ACABOU de perder o Pro (transição tinha→não tem) e ainda não viu o
     * Aviso de rebaixamento. Persistida: sobrevive ao fechamento do app (expirou com o app fechado
     * → mostra na próxima abertura). Limpa ao ser exibida (1× por perda) e re-arma ao reganhar o
     * Pro. Ver docs/adr/0026 (seção "Aviso de rebaixamento").
     */
    downgradeNoticePending: boolean;

    /** Liga/desliga o Pro manualmente (apenas dev/QA). */
    setDevOverride: (on: boolean, plan?: ProPlanId) => void;
    /** Aplica o entitlement vindo do provedor (chamado pelo proService). */
    applyEntitlement: (entitlement: { isPro: boolean; plan: ProPlanId | null }) => void;
    /**
     * Volta ao estado de instalação nova (sem Pro, sem override). O entitlement real
     * do RevenueCat é re-sincronizado no próximo boot do proService — isto só zera o
     * cache local. Usado pelo "Apagar dados" (factory reset).
     */
    reset: () => void;
    /** Limpa a marca do Aviso de rebaixamento (chamado quando o aviso é exibido — 1× por perda). */
    clearDowngradeNotice: () => void;
}

type ResolvedPro = Pick<ProState, 'isPro' | 'activePlan' | 'source'>;

function resolve(
    devOverride: boolean,
    devPlan: ProPlanId | null,
    remoteIsPro: boolean,
    remotePlan: ProPlanId | null,
): ResolvedPro {
    if (devOverride) {
        return { isPro: true, activePlan: devPlan ?? 'annual', source: 'dev' };
    }
    if (remoteIsPro) {
        return { isPro: true, activePlan: remotePlan, source: 'revenuecat' };
    }
    return { isPro: false, activePlan: null, source: null };
}

/**
 * Decide a marca do Aviso de rebaixamento após uma mudança de entitlement:
 * - reganhou / segue Pro (next=true)       → limpa/re-arma (false)
 * - perdeu agora (prev=true, next=false)    → marca (true)
 * - segue não-Pro (prev=false, next=false)  → mantém o que estava (não re-marca free antigo)
 */
function noticePendingAfter(prevIsPro: boolean, nextIsPro: boolean, current: boolean): boolean {
    if (nextIsPro) return false;
    if (prevIsPro) return true;
    return current;
}

export const useProStore = create<ProState>()(
    persist(
        (set, get) => ({
            isPro: false,
            hasHydrated: false,
            activePlan: null,
            source: null,
            devOverride: false,
            devPlan: null,
            remoteIsPro: false,
            remotePlan: null,
            downgradeNoticePending: false,

            setDevOverride: (on, plan) => {
                const devPlan = on ? plan ?? 'annual' : null;
                const { remoteIsPro, remotePlan, isPro: prevIsPro, downgradeNoticePending } = get();
                const resolved = resolve(on, devPlan, remoteIsPro, remotePlan);
                set({
                    devOverride: on,
                    devPlan,
                    ...resolved,
                    downgradeNoticePending: noticePendingAfter(prevIsPro, resolved.isPro, downgradeNoticePending),
                });
            },

            applyEntitlement: ({ isPro, plan }) => {
                const { devOverride, devPlan, isPro: prevIsPro, downgradeNoticePending } = get();
                const resolved = resolve(devOverride, devPlan, isPro, plan);
                set({
                    remoteIsPro: isPro,
                    remotePlan: plan,
                    ...resolved,
                    downgradeNoticePending: noticePendingAfter(prevIsPro, resolved.isPro, downgradeNoticePending),
                });
            },

            reset: () =>
                set({
                    isPro: false,
                    activePlan: null,
                    source: null,
                    devOverride: false,
                    devPlan: null,
                    remoteIsPro: false,
                    remotePlan: null,
                    downgradeNoticePending: false,
                }),

            clearDowngradeNotice: () => set({ downgradeNoticePending: false }),
        }),
        {
            name: '@finapp_pro',
            storage: createJSONStorage(() => AsyncStorage),
            // `hasHydrated` é runtime (não persiste): nasce false a cada processo e só vira true
            // quando a reidratação termina, marcando "já sei o último entitlement".
            partialize: (state) => ({
                isPro: state.isPro,
                activePlan: state.activePlan,
                source: state.source,
                devOverride: state.devOverride,
                devPlan: state.devPlan,
                remoteIsPro: state.remoteIsPro,
                remotePlan: state.remotePlan,
                downgradeNoticePending: state.downgradeNoticePending,
            }),
            onRehydrateStorage: () => () => {
                useProStore.setState({ hasHydrated: true });
            },
        },
    ),
);

export async function hydrateProStore(): Promise<void> {
    try {
        if (useProStore.persist.hasHydrated()) return;
        await useProStore.persist.rehydrate();
    } catch {
        // Mantém o estado inicial se o AsyncStorage falhar.
    }
}

/** Seletor de conveniencia para o status Pro. */
export const useIsPro = () => useProStore((state) => state.isPro);

/**
 * `true` SÓ quando já sabemos (store hidratado) que o usuário NÃO é Pro. Enquanto o status é
 * desconhecido (antes da reidratação) devolve `false` — assim as features Pro NÃO somem por engano
 * antes de termos a resposta do RevenueCat. É o sinal certo para "rebaixar" no consumo (ADR 0026).
 */
export const useIsProDowngraded = () => useProStore((state) => state.hasHydrated && !state.isPro);

/**
 * Versão NÃO-reativa de {@link useIsProDowngraded} para uso fora de React (schedulers, libs):
 * `true` só quando já sabemos que NÃO é Pro. Mantém a mesma regra de "não rebaixar antes de
 * hidratar" em todos os consumidores, reativos ou não (ADR 0026).
 */
export function isProDowngraded(): boolean {
    const state = useProStore.getState();
    return state.hasHydrated && !state.isPro;
}

/**
 * Dual de {@link isProDowngraded}: `true` só quando já sabemos (store hidratado) que o usuário
 * É Pro. Com o status desconhecido devolve `false`.
 *
 * Use este — e não `!isProDowngraded()` — quando o Pro for a **única** trava de uma feature e
 * errar pra mais significa entregá-la de graça. É o caso da captura de vale (ADR 0054): a
 * preferência agora nasce ligada, então se a reidratação falhar no listener headless
 * `isProDowngraded()` devolveria `false` e o não-Pro captaria. Aqui, dúvida = não capta —
 * o custo é uma notificação perdida num cenário raro, não um recurso Pro vazando.
 */
export function isProConfirmed(): boolean {
    const state = useProStore.getState();
    return state.hasHydrated && state.isPro;
}

/**
 * `true` quando há um Aviso de rebaixamento pendente (o usuário perdeu o Pro e ainda não viu a
 * notinha). Consumido pelo `_layout` para abrir a tela `downgrade-notice` 1× por perda. Ver
 * docs/adr/0026.
 */
export const useDowngradeNoticePending = () => useProStore((state) => state.downgradeNoticePending);
